import React, { useEffect, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useDispatch, useSelector } from "react-redux";
import {
  addCompanyCategories,
  addCompanyTags,
  filterCompanyDirectory,
  removeCompanyTags,
  searchCompanyDirectoryVendor,
} from "actions/companyDirectoryActions";
import TableComponent from "./tableComponent";
import ApplyTagsPopUp from "./ApplyTagsPopUp";
import { fetchTag } from "actions/tagsActions";
import { addVendorsToRfq } from "actions/rfqActions";
import ErrorPopup from "components/ErrorPopup";
import Tooltip from '@mui/material/Tooltip';
import SelectedVendorsDrawer from "views/NeevayNetwork/components/tableComponenets/SelectedVendorsDrawer";
import StaticPopupNew from "components/StaticPopupNew";
import VendorBulkUpload from "../VendorBulkUpload";
import VendorInvitationForm from "./VendorInvitationForm";
import AddCategoryPopUp from "./AddCategoryPopUp";
import VendorFormDrawer from "./VendorFormDrawer";


const normalizeRecommendedInternalVendor = (vendor = {}) => ({
  ...vendor,
  id: vendor.vendorId || vendor.id,
  supplierId: vendor.supplierId || vendor.neevaySupplierId || vendor.vendorId || vendor.id,
  Company: vendor.businessName || vendor.Company || vendor.vendorName,
  onNeevay: Boolean(vendor.isNeevayVendor && (vendor.supplierId || vendor.neevaySupplierId)),
  contactDetails: Array.isArray(vendor.contactDetails) ? vendor.contactDetails : [],
});

const getInternalVendorKey = (vendor = {}) =>
  String(
    vendor?.id ||
      vendor?.vendorId ||
      vendor?.neevaySupplierId ||
      vendor?.supplierId ||
      vendor?._id ||
      "",
  );

const getSelectionSnapshotKey = (vendors = []) =>
  vendors
    .map(getInternalVendorKey)
    .filter(Boolean)
    .sort()
    .join("|");

const TableSection = ({
  isSliderView = false,
  rfqId,
  handleSliderClose,
  invitedVendors,
  setOpenSlider,
  setSliderName,
  maxLimit,
  preselectedVendors = [],
  selectionOnly = false,
  onSelectionChange,
  hideCategoryQuickFilter = false,
  isFilterPanelOpen = true,
  onToggleFilters,
  onActiveTabChange,
}) => {
  const dispatch = useDispatch();
  const vendorsPayload = useSelector((state) => state.companyDirectory);
  const companyDirectory = useSelector((state) => state.companyDirectory);
  const auth = useSelector((state) => state.auth);
  const userProfile = auth.user;
  const [isOpen, setIsOpen] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(
    companyDirectory?.searchKey ?? ""
  );
  const [openAddVendorPopup, setOpenAddVendorPopup] = useState(false);
  const [openBulkUpload, setOpenBulkUpload] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [invalidEmailsDetails, setInvalidEmailsDetails] = useState([]);
  const [emailNotAvailableDetails, setEmailNotAvailableDetails] = useState([]);
  const [openEmailError, setOpenEmailError] = useState(false);
  const [showError, setShowError] = useState({ status: false, message: "" });
  const [openDrawer, setOpenDrawer] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openInviteLinkPopup, setOpenInviteLinkPopup] = useState(false);
  const [activeTab, setActiveTab] = useState("APPROVED");
  const [openAddVendorMenu, setOpenAddVendorMenu] = useState(false);
  const [openAddCategoryPopup, setOpenAddCategoryPopup] = useState(false);
  const [showCategoryFilter, setShowCategoryFilter] = useState(true);
  const addVendorMenuRef = useRef(null);
  const hasSelectionSyncStarted = useRef(false);
  const appliedPreselectedSnapshotRef = useRef("");

  useEffect(() => {
    if (!openAddVendorMenu) return undefined;

    const handleOutsideClick = (event) => {
      if (!addVendorMenuRef.current?.contains(event.target)) {
        setOpenAddVendorMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openAddVendorMenu]);

  const vendorTabs = [
    { key: "MY_VENDORS", label: "My Vendors" },
    { key: "APPROVED", label: "Approved Vendors" },
    { key: "PENDING", label: "Pending Approvals" },
    { key: "APPROVED_BY_ME", label: "Approved By Me" },
    { key: "ARCHIVED", label: "Archived" }
  ];

  const getVendorTabCount = (tabKey) => {
    if (tabKey === activeTab) {
      return companyDirectory?.totalCount ?? 0;
    }

    if (tabKey === "APPROVED") {
      return vendorsPayload?.approvedCount;
    }
    if (tabKey === "MY_VENDORS") return vendorsPayload?.myVendorsCount;
    if (tabKey === "PENDING") {
      return isManager
        ? vendorsPayload?.pendingCount
        : vendorsPayload?.myPendingCount;
    }
    if (tabKey === "APPROVED_BY_ME") {
      return isManager
        ? vendorsPayload?.approvedByMeCount
        : vendorsPayload?.myApprovedByMeCount;
    }
    return isManager
      ? vendorsPayload?.archivedCount
      : vendorsPayload?.myArchivedCount;
  };


  const safeInvitedVendors = invitedVendors ?? [];
  const isBulkUpload = userProfile?.userEntity?.features?.some(feature => feature.featureName === "BULK_UPLOAD" && feature.hasFeature);

  // Handle search input

  const appliedFilters = companyDirectory?.appliedFilters;
  const searchKey = companyDirectory?.searchKey;
  const pageNum = companyDirectory?.currentPage;
  const user = auth?.user;
  const userId = user?.userId;
  const userName = user?.name;
  const isManager =
    String(user?.IAM_role || "").trim().toLowerCase() === "manager";
  const userEmail = user?.email;
  const entityId = user?.userEntity?.entityId;
  const senderEmail = user?.userEntity?.senderEmail;
  const gstPanAvailabilityCount =
    (appliedFilters?.gstNumberAvailability === "with" &&
      appliedFilters?.panNumberAvailability === "with") ||
    (appliedFilters?.gstNumberAvailability === "without" &&
      appliedFilters?.panNumberAvailability === "without")
      ? 1
      : 0;
  const activeFilterCount =
    Object.entries(appliedFilters || {}).filter(
    ([key, value]) => {
      if (
        [
          "isApproved",
          "isDeleted",
          "approvedByUserId",
          "createdBy",
          "category",
          "vendorCategory",
          "vendorMake",
          "tags",
          "gstNumberAvailability",
          "panNumberAvailability",
        ].includes(key)
      ) {
        return false;
      }

      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    },
  ).length + gstPanAvailabilityCount;

  useEffect(() => {
    if (!isSliderView) return;

    const normalizedPreselected = (preselectedVendors || []).map(normalizeRecommendedInternalVendor);
    const snapshotKey = getSelectionSnapshotKey(normalizedPreselected);

    if (appliedPreselectedSnapshotRef.current === snapshotKey) return;
    if (hasSelectionSyncStarted.current && snapshotKey) return;

    appliedPreselectedSnapshotRef.current = snapshotKey;
    setSelectedRows(normalizedPreselected);
  }, [isSliderView, preselectedVendors]);

  useEffect(() => {
    if (selectionOnly) {
      if (!hasSelectionSyncStarted.current) {
        hasSelectionSyncStarted.current = true;
        if (!selectedRows.length) return;
      }
      appliedPreselectedSnapshotRef.current = getSelectionSnapshotKey(selectedRows);
      onSelectionChange?.(selectedRows);
    }
  }, [onSelectionChange, selectedRows, selectionOnly]);

  const getTabFilterData = (tabKey) => {
    const createdByFilter = !isManager && userId ? { createdBy: userId } : {};

    if (tabKey === "MY_VENDORS") {
      return { isApproved: true, createdBy: userId };
    }

    if (tabKey === "PENDING") {
      return { isApproved: false, ...createdByFilter };
    }

    if (tabKey === "APPROVED_BY_ME") {
      return { approvedByUserId: userId, ...createdByFilter };
    }

    if (tabKey === "ARCHIVED") {
      return { isDeleted: true, ...createdByFilter };
    }

    return { isApproved: true };
  };

  const handleCloseEmailError = () => {
    setInvalidEmailsDetails([]);
    setEmailNotAvailableDetails([]);
    setOpenEmailError(false);
  };

  const generateInviteLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite-vendor?entityId=${entityId}&entityName=${encodeURIComponent(userProfile?.userEntity?.entityName)}&userId=${userId}&name=${encodeURIComponent(userName)}`;
  };


  const validateEmail = (email) => {
    // Trim the email to remove any leading or trailing spaces
    const trimmedEmail = email.trim();

    // Regular expression to validate the email format
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Test the trimmed email against the regular expression
    return emailPattern.test(trimmedEmail);
  };
  const handleOpenAddVendorPopup = () => {
    setOpenAddVendorPopup(true);
  };

  const handleOpenBulkUpload = () => {
    setOpenBulkUpload(true);
  };

  const handlCloseBulkUpload = () => {
    setOpenBulkUpload(false);
  };

  const handleAddTagsToVendors = async (selectedTags, type) => {
    const payload = {
      entityId: entityId,
      vendorIds: selectedRows?.map((vendor) => vendor?.id),
      userId: userId,
    };
    setTagsLoading(true);

    if (type === "add") {
      payload.tags = selectedTags;
      const result = await dispatch(addCompanyTags(payload));
      setSelectedRows([]);
      handleResult(result, appliedFilters);
    } else if (type === "remove") {
      payload.tagsToRemove = selectedTags;
      const result = await dispatch(removeCompanyTags(payload));
      setSelectedRows([]);
      //when some one remove that tags we ensure this tag is removed from filter also
      const filteredTags = appliedFilters?.tags
        ? appliedFilters?.tags?.filter((tag) => !selectedTags.includes(tag))
        : [];
      const filterData = appliedFilters?.tags
        ? { ...appliedFilters, tags: filteredTags }
        : appliedFilters;

      handleResult(result, filterData);
    }
  };

  const handleResult = (result, filterData) => {
    setTagsLoading(false);
    if (result?.error) {
      setShowError({ status: true, message: result.message });
    } else {
      // setSelectedRows([]);
      setShowError({ status: false, message: "" });
      setIsOpen(false);
      // console.log("this is a filterData", filterData);
      dispatch(
        filterCompanyDirectory({
          filterData,
          searchKey,
          pageNum,
          userId,
          entityId,
        })
      );
    }
  };

  const handleSearchChange = (e) => {
    const searchString = e.target.value;
    setSearchValue(searchString);
    if (searchString === "") {
      dispatch(
        filterCompanyDirectory({
          filterData:
            Object.keys(appliedFilters || {}).length > 0
              ? appliedFilters
              : getTabFilterData(activeTab),
          searchKey: "",
          pageNum: 1,
          userId,
          entityId,
        })
      );
      // setSelectedRows([]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      dispatch(
        searchCompanyDirectoryVendor({ searchKey: searchValue, entityId })
      );
      // setSelectedRows([]);
      e.preventDefault();
    }
  };
  const handleOpen = () => {
    setIsOpen(true);
    dispatch(fetchTag());
  };

  const handleOpenAddCategory = () => {
    setShowError({ status: false, message: "" });
    setOpenAddCategoryPopup(true);
  };

  const handleAddCategoriesToVendors = async (vendorCategories) => {
    if (!vendorCategories?.length) {
      setShowError({ status: true, message: "Please select at least one category." });
      return;
    }

    const payload = {
      entityId,
      vendorIds: selectedRows?.map((vendor) => vendor?.id),
      vendorCategories,
    };

    setCategoryLoading(true);
    const result = await dispatch(addCompanyCategories(payload));
    setCategoryLoading(false);

    if (result?.error) {
      setShowError({
        status: true,
        message: result?.message || "Failed to add categories.",
      });
      return;
    }

    setShowError({ status: false, message: "" });
    setOpenAddCategoryPopup(false);
    setSelectedRows([]);
    setSnackbarSeverity("success");
    setSnackbarMessage(result?.message || "Categories added successfully");
    setSnackbarOpen(true);

    dispatch(
      filterCompanyDirectory({
        filterData: appliedFilters,
        searchKey,
        pageNum,
        userId,
        entityId,
      })
    );
  };
  const handleAddVendorsToRfq = () => {
    if (safeInvitedVendors?.length + selectedRows.length > maxLimit) {
      setSnackbarSeverity("error");
      setSnackbarMessage(`You can add only ${maxLimit} vendors to this RFQ.`);
      setSnackbarOpen(true);
      return;
    }
    const invalidEmail = [];
    const emailNotAvailable = [];
    const suppliers = selectedRows?.map((row) => {
      var emailExists = false;
      const contactDetails = row?.contactDetails.map((contact) => {
        // Create a new object to avoid modifying the original one
        if (contact.email) {
          if (!validateEmail(contact.email)) {
            invalidEmail.push({
              businessName: row?.Company,
              supplierId: row?.supplierId?.toString() || row?.id,
              email: contact.email,
            });
            emailExists = true;
          } else {
            emailExists = true;
          }
        }
        return {
          ...contact,
          name: contact.hasOwnProperty("name") ? contact.name : "User",
        };
      });
      if (!emailExists) {
        emailNotAvailable.push({
          businessName: row?.Company,
          supplierId: row?.supplierId?.toString() || row?.id,
        });
      }
      const supplierObj = row?.onNeevay
        ? {
          supplierId: row?.supplierId?.toString() || row?.id,
          businessName: row?.Company,
          isNeevayVendor: row?.onNeevay,
          isPremium: row?.isPremium,
          contactDetails: contactDetails,
          vendorSource: "INTERNAL"
        }
        : {
          supplierId: row?.id || row?.supplierId?.toString() || null,
          businessName: row?.Company,
          isNeevayVendor: row?.onNeevay,
          contactDetails: contactDetails,
          vendorSource: "INTERNAL"
        };
      return { ...supplierObj, addedBy: { name: userName, userId: userId } };
    });
    if (invalidEmail?.length === 0 && emailNotAvailable?.length === 0) {
      dispatch(
        addVendorsToRfq({
          rfqId,
          suppliers,
          requesterName: userName,
          requestedBy: userEmail,
          senderEmail
        })
      );
      handleSliderClose();
      setSliderName("NN")
      setOpenSlider(true);
      setSelectedRows([]);
      // navigate(`/rfqs/${rfqId}/Vendors`);
    } else {
      setInvalidEmailsDetails(invalidEmail);
      setEmailNotAvailableDetails(emailNotAvailable);
      setOpenEmailError(true);
    }
  };


  const handleSelectedVendorsClick = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };



  useEffect(() => {
    dispatch(
      filterCompanyDirectory({
        filterData: getTabFilterData(activeTab),
        searchKey: "",
        pageNum: 1,
        userId,
        entityId,
      })
    );
    // Keep this as an initial/user-context load. Tab clicks dispatch their own filter request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, entityId, userId, isManager]);
  useEffect(() => {
    setSearchValue(companyDirectory?.searchKey ?? "");
  }, [companyDirectory]);
  // <section className="h-10 sticky top-0 z-20 bg-gray-100 flex items-center"></section>
  return (
    <>
      <div className={`h-full min-h-0 flex flex-col ${selectionOnly ? "gap-3" : "gap-[10px]"}`}>
        <section className="shrink-0">
          <div className="rounded-xl bg-white/90 px-3 shadow-sm">
            <div className="flex w-full flex-nowrap items-center overflow-visible pb-1 pt-3">
              <div className="flex shrink-0 items-center gap-3">
                {!isSliderView && (
                  <button
                    type="button"
                    onClick={onToggleFilters}
                    className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition ${
                      isFilterPanelOpen
                        ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                        : "border-[#DADAD0] bg-white text-[#303026] hover:border-[#E95420] hover:text-[#B43A12]"
                    }`}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 5h18M6 12h12M10 19h4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    Filters
                    {/* {activeFilterCount > 0 && (
                      <span className="rounded-full bg-[#E95420] px-1.5 py-[1px] text-[10px] text-white">
                        {activeFilterCount}
                      </span>
                    )} */}
                  </button>
                )}

                {!isSliderView && !hideCategoryQuickFilter && (
                  <button
                    type="button"
                    onClick={() => setShowCategoryFilter((prev) => !prev)}
                    className={`relative inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition ${
                      showCategoryFilter
                        ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                        : "border-[#DADAD0] bg-white text-[#303026] hover:border-[#E95420] hover:text-[#B43A12]"
                    }`}
                  >
                    <span className="absolute -left-1 -top-1 flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E95420] opacity-40"></span>
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E95420]"></span>
                    </span>
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    Categories
                  </button>
                )}
              </div>
            <div className="flex items-center justify-between w-full">
              <div className={selectionOnly ? "w-[400px] shrink-0" : "min-w-[220px] flex-1"}>
                <div className="relative mx-3">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>

                  <input
                    type="text"
                    className="block w-full p-2 pl-9 text-xs border border-gray-300 rounded-lg bg-[#FCFCFA] focus:border-[#E95420] focus:ring-0 focus:ring-[#E95420]"
                    placeholder="Search by Business Name, WhatsApp Number, Email, GSTIN"
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
              {isSliderView && <p className="shrink-0 text-xs text-gray-700">
                {pageNum && (
                  <>
                    {`Showing ${(pageNum - 1) * 50 + 1} to ${(pageNum - 1) * 50 +
                      companyDirectory?.vendors?.length
                      } of ${companyDirectory?.totalCount} total vendors`}
                    {companyDirectory?.searchKey && (
                      <span>
                        {" "}for{" "}
                        <span className="text-orange-500 font-semibold">
                          "{companyDirectory.searchKey}"
                        </span>
                      </span>
                    )}
                  </>
                )}
              </p>}
            </div>  
              

            <div className="ml-auto flex shrink-0 items-center gap-3 relative">
              {/* ADD VENDOR DROPDOWN */}
              <div ref={addVendorMenuRef} className="relative">
                {!isSliderView && (
                  <>
                    <button
                      className="whitespace-nowrap px-4 py-2 bg-black text-white text-xs rounded-md font-medium"
                      onClick={() => setOpenAddVendorMenu(prev => !prev)}
                    >
                      + Add New Vendor
                    </button>

                    {openAddVendorMenu && (
                      <div className="absolute right-0 top-full z-[90] mt-1 w-fit rounded-md border border-gray-200 bg-white shadow-lg">
                        <button
                          className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                          onClick={() => {
                            setOpenAddVendorMenu(false);
                            handleOpenAddVendorPopup();
                          }}
                        >
                          Add Manually
                        </button>

                        {isBulkUpload && (
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                            onClick={() => {
                              setOpenAddVendorMenu(false);
                              handleOpenBulkUpload();
                            }}
                          >
                            Bulk Upload
                          </button>
                        )}

                        <button
                          className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                          onClick={() => {
                            setOpenAddVendorMenu(false);
                            setOpenInviteLinkPopup(true);
                          }}
                        >
                          Send Invitation
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>


              {/* RIGHT SIDE BUTTONS */}
              <div className="flex shrink-0 items-center gap-2">

                {/* SELECTED VENDORS */}
                {!selectionOnly && selectedRows?.length > 0 && (
                  <div className="relative">
                    <button
                      className="p-2 border border-black hover:shadow-md rounded-md text-xs font-medium"
                      onClick={handleSelectedVendorsClick}
                    >
                      Selected Vendors
                    </button>

                    <span className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                      {selectedRows.length}
                    </span>
                  </div>
                )}

                {/* SLIDER VIEW */}
                {isSliderView && !selectionOnly ? (
                  <button
                    disabled={selectedRows.length < 1}
                    onClick={handleAddVendorsToRfq}
                    className={`rounded-md text-xs px-3 py-2
                 ${selectedRows.length >= 1
                        ? "bg-black text-white"
                        : "bg-gray-300 text-gray-100 cursor-not-allowed"
                      }`}
                  >
                    Invite Vendors
                  </button>
                ) : !isSliderView ? (
                  <>
                    {/* Temporarily hidden as requested. Keep this code for easy rollback when tags are needed again.
                    <Tooltip
                      title={selectedRows.length < 1 ? "Select at least one vendor to apply tags" : ""}
                      disableInteractive
                    >
                      <button
                        className={`px-3 py-2 text-xs rounded-sm
                      ${selectedRows.length >= 1
                            ? "border border-black hover:shadow-md font-medium"
                            : "bg-gray-300 text-gray-100 cursor-not-allowed"
                          }`}
                        onClick={handleOpen}
                        disabled={selectedRows.length < 1}
                      >
                        Apply Tags
                      </button>
                    </Tooltip>
                    */}
                    <Tooltip
                      title={selectedRows.length < 1 ? "Select at least one vendor to add category" : ""}
                      disableInteractive
                    >
                      <button
                        className={`px-3 py-2 text-xs rounded-md
                      ${selectedRows.length >= 1
                            ? "border border-black hover:shadow-md font-medium"
                            : "bg-gray-400 text-gray-100 cursor-not-allowed"
                          }`}
                        onClick={handleOpenAddCategory}
                        disabled={selectedRows.length < 1}
                      >
                        Update Vendor Categories
                      </button>
                    </Tooltip>
                  </>
                ) : null}

              </div>
              </div>
            </div>

            <div className={`${isSliderView ? "mt-0" : "mt-4"} flex w-full flex-wrap items-end justify-between gap-3 mx-2`}>
              {!isSliderView && (
                <div className="flex min-w-0 flex-1 gap-6 overflow-x-auto text-sm">
                  {vendorTabs.map((tab) => {
                    const count = getVendorTabCount(tab.key);
                    const isPendingTab = tab.key === "PENDING";

                    return (
                      <button
                        key={tab.key}
                        onClick={() => {
                          setActiveTab(tab.key);
                          onActiveTabChange?.(tab.key);

                          const filterData = getTabFilterData(tab.key);

                          dispatch(
                            filterCompanyDirectory({
                              filterData,
                              searchKey: "",
                              pageNum: 1,
                              userId,
                              entityId,
                            })
                          );
                        }}
                        className={`whitespace-nowrap pb-1 text-left transition ${
                          activeTab === tab.key
                            ? "font-semibold border-b-2 border-orange-500 text-black"
                            : "text-gray-600 hover:text-black"
                        }`}
                      >
                        {tab.label}
                        {isPendingTab ? (
                          <span className="ml-1 inline-flex h-4 min-w-[16px] -translate-y-1 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] leading-none text-white">
                            {count ?? 0}
                          </span>
                        ) : (
                          <>
                            {" "}
                            <span className="text-xs text-gray-500">
                              ({count ?? 0})
                            </span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {!isSliderView && <p className="shrink-0 text-xs text-gray-700 p-2">
                {pageNum && (
                  <>
                    {`Showing ${(pageNum - 1) * 50 + 1} to ${(pageNum - 1) * 50 +
                      companyDirectory?.vendors?.length
                      } of ${companyDirectory?.totalCount} total vendors`}
                    {companyDirectory?.searchKey && (
                      <span>
                        {" "}for{" "}
                        <span className="text-orange-500 font-semibold">
                          "{companyDirectory.searchKey}"
                        </span>
                      </span>
                    )}
                  </>
                )}
              </p>}
            </div>
          </div>

        </section>
        <div
          className="min-h-0 flex-grow overflow-y-auto pr-1"
          style={{ scrollbarWidth: "none", "-ms-overflow-style": "none" }}
        >
          <TableComponent
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            searchValue={searchValue}
            invitedVendors={safeInvitedVendors}
            maxLimit={maxLimit}
            isSliderView={isSliderView}
            activeTab={activeTab}
            hideCategoryQuickFilter={
              hideCategoryQuickFilter || (!isSliderView && !showCategoryFilter)
            }
          />
        </div>
      </div>

      <div>
        {/* Temporarily hidden with the Apply Tags button. */}
        {false && isOpen && (
          <ApplyTagsPopUp
            tagsLoading={tagsLoading}
            setIsOpen={setIsOpen}
            handleAddTagsToVendors={handleAddTagsToVendors}
            showError={showError}
          />
        )}
      </div>

      <AddCategoryPopUp
        open={openAddCategoryPopup}
        onClose={() => setOpenAddCategoryPopup(false)}
        onApply={handleAddCategoriesToVendors}
        loading={categoryLoading}
        selectedVendors={selectedRows}
        showError={showError}
      />

      {/* <SlidingCompanyInfo isOpen={isOpen} setIsOpen={setIsOpen} /> */}
      {/* <VendorForm
        open={openAddVendorPopup}
        onClose={handleCloseAddVendorPopup}
        userProfile={userProfile}
        onVendorAdded={() => setSnackbarOpen(true)}
      /> */}
      <VendorFormDrawer
        open={openAddVendorPopup}
        onClose={() => setOpenAddVendorPopup(false)}
        header="Add Vendor"
      >
        <VendorInvitationForm
          user={userProfile}
          isEdit={false}
          isApprovalMode={false}
          isPopupView={true}
          submissionSource="MANUAL"
          onSuccess={() => {
            setOpenAddVendorPopup(false);
            setSnackbarOpen(true);
          }}
        />
      </VendorFormDrawer>

      <ErrorPopup
        openModal={openEmailError}
        handleClosePopup={() => handleCloseEmailError(false)}
        size={"xl"}
        warningText={
          <p className="flex flex-col text-center">
            {
              <p className="mb-2 mt-[-8px] underline">
                Please note the following regarding vendors:
              </p>
            }
            {invalidEmailsDetails?.length >= 1 && (
              <span className="flex flex-col gap-1 mb-2">
                {invalidEmailsDetails.map((details) => {
                  return (
                    <p className="">
                      <span className="text-gray-800">
                        {details?.businessName}
                      </span>
                      : Invalid email address - <i>{details?.email}</i>
                    </p>
                  );
                })}
              </span>
            )}
            {emailNotAvailableDetails?.length >= 1 && (
              <span className="flex flex-col gap-1 mb-2">
                {emailNotAvailableDetails?.map((data) => {
                  return (
                    <p className="">
                      <span className="text-gray-800">
                        {data?.businessName}{" "}
                      </span>
                      {`: No email address provided.`}
                    </p>
                  );
                })}
              </span>
            )}
            <p>
              Kindly deselect these vendors and update the information
              accordingly. Thank you.
            </p>
          </p>
        }
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage || "Vendor added successfully"}
        </MuiAlert>
      </Snackbar>

      <SelectedVendorsDrawer
        openDrawer={openDrawer}
        handleCloseDrawer={handleCloseDrawer}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        handleAddVendorsToRfq={handleAddVendorsToRfq}
        handleSelectedVendorsClick={handleSelectedVendorsClick}
        handleOpen={handleOpen}
        handleOpenAddCategory={handleOpenAddCategory}
        handleOpenAddVendorPopup={handleOpenAddVendorPopup}
        isSliderView={isSliderView}
        selectedRowsLength={selectedRows.length}
      />

      {openBulkUpload && (
        <StaticPopupNew header={"Vendor Bulk Upload"} bodyStyle={`pb-0 pt-0`} openModal={openBulkUpload} handleClose={handlCloseBulkUpload}>
          <VendorBulkUpload userProfile={userProfile} handlCloseBulkUpload={handlCloseBulkUpload} />
        </StaticPopupNew>
      )}

      <StaticPopupNew
        openModal={openInviteLinkPopup}
        handleClose={() => setOpenInviteLinkPopup(false)}
        header={"Invite Vendor"}
        size="sm"
      >
        <div className="p-4">
          <p className="text-sm mb-3">
            Kindly copy the below link to invite a vendor to your internal directory.
          </p>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={generateInviteLink()}
              readOnly
              className="w-full border px-2 py-1 text-xs rounded"
            />
            <button
              className="px-3 py-1 bg-black text-white text-xs rounded"
              onClick={() => {
                navigator.clipboard.writeText(generateInviteLink());
                setSnackbarMessage("Invite link copied!");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
              }}
            >
              Copy
            </button>
          </div>
        </div>
      </StaticPopupNew>


    </>
  );
};

export default TableSection;
